import { ChartItemData, LineChartItem } from "src/components/monitoring/senec-line-chart/lineChartTypes";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";
import { Stack, useTheme } from "@mui/material";
import { TimeFrame } from "src/components/monitoring/commonTypes";
import dayjs, { Dayjs } from "dayjs";

interface StatusAxisProps {
    series?: LineChartItem;
    selectedDate?: Date;
    timeframe: TimeFrame;
}

export const StatusLine = ({ series, selectedDate, timeframe }: StatusAxisProps) => {
    if (!series || series.data.length <= 0) return <></>;

    const theme: SenecBaseTheme = useTheme();
    const colorSelected = series.color;
    const colorInactive = theme.palette.senecSecondary.neutralGray10;

    const statuses = getStatuses(
        series.data,
        colorSelected,
        colorInactive,
        timeframe,
        selectedDate !== undefined ? dayjs(selectedDate) : undefined,
    );
    const totalDuration = dayjs(timeframe.end).diff(dayjs(timeframe.start), "second");

    const calculateWidthPercentage = (duration: number, totalDuration: number) => {
        return (duration / totalDuration) * 100 + "%";
    };

    return (
        <Stack
            data-testid={"status-line"}
            direction="row"
            px={2}
            style={{ position: "absolute", bottom: "-20px", width: "100%", height: "10px" }}
        >
            <div className="timeline" style={{ display: "flex", width: "100%" }}>
                {statuses.map((status, index) => (
                    <div
                        data-testid={"status-line-entry-" + index}
                        key={"status-entry-" + index}
                        className={`status-${status.status}`}
                        style={{
                            width: calculateWidthPercentage(status.duration, totalDuration),
                            backgroundColor: status.color,
                            color: status.color,
                            borderRadius: "15px",
                            marginRight: "2px",
                        }}
                    ></div>
                ))}
            </div>
        </Stack>
    );
};

const getStatusLineColor = (
    startTime: Dayjs,
    endTime: Dayjs,
    colorSelected: string,
    colorInactive: string,
    selectedDate?: Dayjs,
) => {
    if (selectedDate && (startTime.isBefore(selectedDate) || startTime.isSame(selectedDate)) && endTime.isAfter(selectedDate)) {
        return colorSelected;
    }
    return colorInactive;
};

const getStatuses = (
    points: ChartItemData[],
    colorSelected: string,
    colorInactive: string,
    timeframe: TimeFrame,
    selectedDate?: Dayjs,
) => {
    const getStatusLineData = (startTime: Dayjs, endTime: Dayjs, status: number, show = true) => {
        return {
            status: status,
            duration: endTime.diff(startTime, "second"),
            color: show ? getStatusLineColor(startTime, endTime, colorSelected, colorInactive, selectedDate) : "transparent",
        };
    };

    const statuses = [];
    let startTime: Dayjs = dayjs(points[0].x);
    let status = points[0].y as number;

    // add empty status data, if data starts after start date
    if (dayjs(timeframe.start).isBefore(startTime)) {
        const statusData = getStatusLineData(dayjs(timeframe.start), startTime, 0, false);
        statuses.push(statusData);
    }

    // add status data
    for (const point of points) {
        const currentStartTime: Dayjs = dayjs(point.x);
        const currentStatus = point.y as number;

        if (currentStatus !== status) {
            const statusData = getStatusLineData(startTime, currentStartTime, status);
            statuses.push(statusData);

            startTime = currentStartTime;
            status = currentStatus;
        }
    }

    // add last status data
    const endTime = dayjs(points[points.length - 1].x);
    const lastStatusData = getStatusLineData(startTime, endTime, status);
    statuses.push(lastStatusData);

    return statuses;
};
